<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors }">
        <modal
            @hidden="clearData"
            @shown="setData"
            :id="modalId"
            :ok-disabled="!meta.valid"
            :title="title"
            @ok.prevent="submit"
        >
            <validation-provider name="title" rules="required">
                <form-input
                    :error="errors.title"
                    v-model="values.title"
                    label="Name"
                    @input="(val) => setFieldValue('title', val)"
                />
            </validation-provider>
            <div class="d-flex justify-content-between input-wrapper align-items-baseline flex-md-row flex-column">
                <validation-provider name="value" class="w-100" rules="required|validNumber" as="div">
                    <form-input
                        class="mb-md-3 mb-0"
                        :error="errors.value"
                        v-model="values.value"
                        label="Value"
                        @input="(val) => setFieldValue('value', val)"
                    />
                </validation-provider>
                <form-select class="mb-md-3 mb-0 w-100" :options="valueOptions" v-model="values.is_flat" />
                <validation-provider name="frequency" v-if="values.is_flat" class="w-100" rules="required" as="div">
                    <form-select
                        :options="frequencyOptions"
                        :error="errors.frequency"
                        v-model="values.frequency"
                        @change="(val) => setFieldValue('frequency', val)"
                    />
                </validation-provider>
            </div>
            <div class="d-flex justify-content-between input-wrapper align-items-baseline">
                <form-select
                    :options="conditionOptions"
                    v-model="values.is_advanced"
                    label="Conditional?"
                    class="w-100"
                />
                <validation-provider name="type" class="w-100" rules="required" as="div">
                    <form-select
                        :options="taxOptions"
                        :error="errors.type"
                        v-model="values.type"
                        label="Type"
                        @change="(val) => setFieldValue('type', val)"
                    />
                </validation-provider>
            </div>
            <div class="d-flex justify-content-between input-wrapper align-items-baseline" v-if="values.is_advanced">
                <validation-provider name="values.value" class="w-100" rules="validPositiveOrZeroFloat" as="div">
                    <form-input
                        :error="errors['values.value']"
                        v-model="values.values.value"
                        :leftButton="values.is_flat"
                        :rightIcon="!values.is_flat ? ['fas', 'percent'] : ''"
                        @input="(val) => setFieldValue('values.value', val)"
                    >
                        <template v-if="values.is_flat" #leftButton>
                            <span> {{ userCurrencySymbol }}</span>
                        </template>
                    </form-input>
                </validation-provider>
                <span>after</span>
                <validation-provider name="values.nights" class="w-100" rules="validPositiveOrZeroFloat" as="div">
                    <form-input
                        :error="errors['values.nights']"
                        v-model="values.values.nights"
                        @input="(val) => setFieldValue('values.nights', val)"
                    />
                </validation-provider>
                <span>nights</span>
            </div>
            <small> Edits will appear on all properties where this deposit currently appears. </small>
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import formInput from '../../generic/form-input/form-input.vue';
    import formSelect from '../../generic/form-select/form-select.vue';
    import modal from '../../generic/modal/modal.vue';

    export default {
        name: 'AddEditTaxModal',
        components: {
            formInput,
            formSelect,
            modal,
        },
        props: {
            initialData: {
                type: Object,
                default: null,
            },
            modalId: {
                type: String,
            },
        },
        data() {
            return {
                values: {
                    title: 'New Tax',
                    value: '',
                    is_flat: false,
                    is_advanced: false,
                    id: null,
                    type: 15,
                    frequency: 'ONETIME',
                    values: {
                        value: '0',
                        nights: 1,
                    },
                },
            };
        },
        computed: {
            ...mapGetters('propertyManager', ['metadata']),
            ...mapGetters('header', ['userCurrencySymbol']),
            title() {
                return this.initialData ? 'Edit Tax' : 'Add Tax';
            },
            preAuthOptions() {
                return [
                    { value: true, text: 'Yes' },
                    { value: false, text: 'No' },
                ];
            },
            valueOptions() {
                return [
                    {
                        value: false,
                        text: 'Percent',
                    },
                    {
                        value: true,
                        text: 'Fixed',
                    },
                ];
            },
            frequencyOptions() {
                return [
                    {
                        value: 'DAILY',
                        text: 'Daily',
                    },
                    {
                        value: 'ONETIME',
                        text: 'One Time',
                    },
                ];
            },
            conditionOptions() {
                return [
                    {
                        value: false,
                        text: 'No',
                    },
                    {
                        value: true,
                        text: 'Yes',
                    },
                ];
            },
            taxOptions() {
                return this.metadata?.tax_type_choices.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
            },
        },
        methods: {
            submit() {
                this.$emit('submit', {
                    ...this.values,
                    type: {
                        id: this.values.type,
                        name: this.taxOptions.find((item) => item.value === this.values.type).text,
                    },
                });
            },
            setData() {
                if (this.initialData) {
                    this.values = {
                        ...this.initialData,
                        type: this.initialData.type.id,
                        values: {
                            value: this.initialData.values?.value || '0',
                            nights: this.initialData.values?.nights || 1,
                        },
                    };
                }
                this.$refs.form.setValues({
                    ...this.values,
                });
            },
            clearData() {
                this.$emit('hide');
                this.values = {
                    title: 'New Tax',
                    value: '',
                    is_flat: false,
                    is_advanced: false,
                    id: null,
                    type: 15,
                    frequency: 'ONETIME',
                    values: {
                        value: '0',
                        nights: 1,
                    },
                };
            },
        },
        watch: {
            initialData() {
                this.setData();
            },
        },
    };
</script>

<style scoped>
    .input-wrapper {
        gap: 1rem;
    }
</style>
